import { fg } from '@atlaskit/platform-feature-flags';
import { withAnalyticsEvents, type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { type UFOExperience, UFOExperienceState } from '@atlaskit/ufo';
import debounce from 'lodash/debounce';
import React from 'react';
import { FormattedMessage } from 'react-intl';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';
import {
	cancelEvent,
	clearEvent,
	createAndFireEventInElementsChannel,
	deleteEvent,
	type EventCreator,
	failedEvent,
	focusEvent,
	searchedEvent,
	selectEvent,
	startSession,
	type UserPickerSession,
} from '../analytics';
import type {
	Appearance,
	AtlasKitSelectChange,
	DefaultValue,
	GroupedOptions,
	InputActionTypes,
	LoadOptions,
	LoadUserSource,
	OnChange,
	OnInputChange,
	OnOption,
	OnPicker,
	Option,
	OptionData,
	UserPickerProps,
	UserPickerRef,
	UserPickerState,
	Value,
} from '../types';
import { batchByKey } from './batch';
import { messages } from './i18n';
import {
	callCallback,
	extractOptionValue,
	getOptions,
	isIterable,
	isPopupUserPickerByComponent,
	isDefaultValuePopulated,
	isSingleValue,
	optionToSelectableOptions,
} from './utils';
import { groupOptionsByType } from '../util/group-options-by-type';
import { userPickerOptionsShownUfoExperience } from '../util/ufoExperiences';
import type { AriaAttributes } from 'react';
import type { SelectComponentsConfig, PopupSelectProps, StylesConfig } from '@atlaskit/select';
import type { EmailValidator } from './emailValidation';

export type BaseUserPickerProps = UserPickerProps & {
	components: any;
	name?: string;
	pickerProps?: any;
	SelectComponent: React.ComponentType<any>;
	styles: any;
	// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required
	/**
	 * @deprecated This is a temporary prop to enable user-pickers to work in Draggable elements in react-beautiful-dnd.
	 * See https://product-fabric.atlassian.net/browse/DSP-15701 for more details.
	 * It may be removed in a future minor or patch when a longer-term workaround is found.
	 */
	UNSAFE_hasDraggableParentComponent?: boolean;
	width: string | number;
};

const loadingMessage = () => null;

const classNamePrefix = 'fabric-user-picker';
export class BaseUserPickerWithoutAnalytics extends React.Component<
	BaseUserPickerProps,
	UserPickerState
> {
	static defaultProps = {
		isMulti: false,
		subtle: false,
		noBorder: false,
		textFieldBackgroundColor: false,
		isClearable: true,
		loadOptionsErrorMessage: (): React.JSX.Element => (
			<FormattedMessage {...messages.errorMessage} />
		),
		openMenuOnClick: false,
	};

	static getDerivedStateFromProps(
		nextProps: Partial<UserPickerProps>,
		prevState: UserPickerState,
	): Partial<UserPickerState> {
		const derivedState: Partial<UserPickerState> = {};
		if (nextProps.isDisabled || nextProps.disableInput) {
			derivedState.menuIsOpen = false;
		}
		if (nextProps.open !== undefined) {
			derivedState.menuIsOpen = nextProps.open;
		}
		if (nextProps.value !== undefined) {
			derivedState.value = optionToSelectableOptions(nextProps.value);
		} else if (isDefaultValuePopulated(nextProps.defaultValue) && !prevState.isDefaultSet) {
			derivedState.value = optionToSelectableOptions(nextProps.defaultValue);
			derivedState.isDefaultSet = true;
		}
		if (nextProps.search !== undefined && nextProps.search !== prevState.inputValue) {
			derivedState.inputValue = nextProps.search;
		}

		if (nextProps.options !== undefined) {
			derivedState.options = nextProps.options;
		}

		return derivedState;
	}

	private selectRef: any | null;

	private session?: UserPickerSession;

	// session id for focus to blur
	private journeyId?: string;

	private optionsShownUfoExperienceInstance: UFOExperience;

	constructor(props: BaseUserPickerProps) {
		super(props);
		this.state = {
			options: [],
			isDefaultSet: false,
			inflightRequest: 0,
			count: 0,
			hoveringClearIndicator: false,
			menuIsOpen: !!this.props.open,
			inputValue: props.search || '',
			resolving: false,
			showError: false,
			initialFocusHandled: false,
		};
		this.optionsShownUfoExperienceInstance =
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			userPickerOptionsShownUfoExperience.getInstance(uuidv4());

		if (this.props.forwardedRef) {
			const refValue = { focus: this.focus, blur: this.blur };
			if (typeof this.props.forwardedRef === 'function') {
				this.props.forwardedRef(refValue);
			} else {
				this.props.forwardedRef.current = refValue;
			}
		}
	}

	private getSessionId = () => (this.session && this.session.id ? this.session.id : undefined);

	private withSelectRef = (callback: (selectRef: any) => void) => (): void => {
		if (this.selectRef) {
			callback(this.selectRef);
		}
	};

	public nextOption: () => void = this.withSelectRef((select) => select.focusOption('down'));

	public previousOption: () => void = this.withSelectRef((select) => select.focusOption('up'));

	public focus = (): void => {
		if (this.selectRef && this.selectRef.focus) {
			this.selectRef.focus();
		}
	};

	public blur = (): void => {
		if (this.selectRef && this.selectRef.blur) {
			this.selectRef.blur();
		}
	};

	public selectOption: () => void = this.withSelectRef((select) => {
		const focusedOption = select.state.focusedOption;
		select.selectOption(focusedOption);
	});

	// value = null if user deletes the last item in the user picker
	private handleChange: AtlasKitSelectChange = (value, { action, removedValue, option }) => {
		if (removedValue && removedValue.data.fixed) {
			return;
		}
		this.resetInputState();
		const { onChange, onSelection, onClear, isMulti } = this.props;
		callCallback(onChange, extractOptionValue(value), action);

		switch (action) {
			case 'select-option':
				if (value) {
					callCallback(onSelection, extractOptionValue(value), this.getSessionId(), this);
				}
				this.fireEvent(selectEvent, isMulti ? option : value);
				this.session = isMulti ? startSession() : undefined;
				break;
			case 'clear':
				callCallback(onClear);
				this.fireEvent(clearEvent);
				break;
			case 'remove-value':
			case 'pop-value':
				if (removedValue) {
					this.fireEvent(deleteEvent, removedValue.data);
				}
				break;
		}
		this.fireEvent(searchedEvent);

		if (removedValue && this.state.value && Array.isArray(this.state.value)) {
			const remainingValues = this.state.value.filter(
				(val: Option) => val.data.id !== removedValue.data.id,
			);
			this.setState({
				value: remainingValues ? remainingValues : [],
				menuIsOpen: true,
			});
		}

		if (!this.props.value) {
			this.setState({ value: value ? value : [] });
		}
	};

	private handleSelectRef = (ref: any | null) => {
		this.selectRef = ref;
	};

	private addOptions = batchByKey((request: string, newOptions: (OptionData | OptionData[])[]) => {
		const { resolving } = this.state;

		this.setState(({ inflightRequest, options, count }) => {
			if (inflightRequest.toString() === request) {
				return {
					options: (resolving ? options : []).concat(
						newOptions.reduce<OptionData[]>(
							(nextOptions, item) =>
								Array.isArray(item) ? nextOptions.concat(item[0]) : nextOptions.concat(item),
							[],
						),
					),
					count: count - newOptions.length,
					resolving: count - newOptions.length !== 0,
				};
			}
			return null;
		});
	});

	private handleLoadOptionsError = () => {
		const { count } = this.state;
		const newCount = count - 1;
		const resolving = newCount !== 0;
		let showError = false;

		if (!resolving) {
			showError = true;
		}

		this.setState({
			count: newCount,
			resolving,
			showError,
		});
		this.fireEvent(failedEvent);
	};

	private debouncedLoadOptions = debounce((search?: string) => {
		const { loadOptions } = this.props;
		if (loadOptions) {
			const inflightRequest = this.state.inflightRequest + 1;
			const result =
				this.session && this.session.id
					? loadOptions(search, this.session.id)
					: loadOptions(search);
			const addOptions = this.addOptions.bind(this, inflightRequest.toString()) as (
				value: OptionData | OptionData[],
			) => void | PromiseLike<void>;
			let count = 0;
			if (isIterable(result)) {
				for (const value of result) {
					Promise.resolve(value).then(addOptions).catch(this.handleLoadOptionsError);
					count++;
				}
			} else {
				Promise.resolve(result).then(addOptions).catch(this.handleLoadOptionsError);
				count++;
			}
			this.setState({
				inflightRequest,
				count,
				resolving: count !== 0,
				options: [],
			});
		}
	}, 200);

	private get isMenuOpenOnClickForSingleSelect() {
		return this.props.openMenuOnClick && !this.props.isMulti;
	}

	abortOptionsShownUfoExperience = (): void => {
		if (this.optionsShownUfoExperienceInstance.state.id === UFOExperienceState.STARTED.id) {
			// There may be an existing UFO timing running from previous key entry or focus,
			// so abort it and restart it just in case.
			this.optionsShownUfoExperienceInstance.abort();
		}
	};

	startOptionsShownUfoExperience = (): void => {
		this.abortOptionsShownUfoExperience();
		this.optionsShownUfoExperienceInstance.start();
	};

	private executeLoadOptions = (search?: string) => {
		const { loadOptions } = this.props;
		if (loadOptions) {
			this.setState({ resolving: true, showError: false }, () => this.debouncedLoadOptions(search));
		}
	};

	private handleFocus = (event: React.FocusEvent) => {
		const { value, menuIsOpen } = this.state;
		if (!menuIsOpen || !this.session) {
			this.startSession();
		}
		this.startOptionsShownUfoExperience();
		callCallback(this.props.onFocus, this.getSessionId());
		const openMenuOnFocus = this.props.openMenuOnFocus ?? true;
		if (!this.isMenuOpenOnClickForSingleSelect && openMenuOnFocus) {
			this.setState({ menuIsOpen: true });
			if (!this.props.isMulti && isSingleValue(value)) {
				const input = event.target;
				this.setState({ inputValue: value.label }, () => {
					if (input instanceof HTMLInputElement) {
						input.setSelectionRange(0, value.label.length);
					}
				});
			}
		}
	};

	private resetInputState = () => {
		// Prevent filter if query typed, then blurred with no selection
		this.setState({
			inputValue: '',
		});
		callCallback(this.props.onInputChange, '', this.getSessionId());
	};

	private handleOpen = () => {
		callCallback(this.props.onOpen, this.getSessionId());
		this.setState({
			menuIsOpen: true,
		});
	};

	private handleBlur = () => {
		if (this.props.isFooterFocused || this.props.isHeaderFocused) {
			return;
		}
		callCallback(this.props.onBlur, this.getSessionId());
		if (isPopupUserPickerByComponent(this.props.SelectComponent)) {
			return;
		}

		this.resetInputState();
		this.abortOptionsShownUfoExperience();
		this.setState({
			menuIsOpen: false,
			initialFocusHandled: false,
			options: [],
		});
	};

	private handleClose = () => {
		if (this.props.isFooterFocused || this.props.isHeaderFocused) {
			return;
		}

		this.resetInputState();
		callCallback(this.props.onClose, this.getSessionId());
		this.setState({
			menuIsOpen: false,
			options: [],
		});
	};

	private handleInputChange = (search: string, { action }: { action: InputActionTypes }) => {
		if (action === 'input-change' || action === 'set-value') {
			callCallback(this.props.onInputChange, search, this.getSessionId());
			this.setState({ inputValue: search });

			this.startOptionsShownUfoExperience();
		}
	};

	private fireEvent = (eventCreator: EventCreator, ...args: any[]) => {
		const { createAnalyticsEvent } = this.props;
		if (createAnalyticsEvent) {
			createAndFireEventInElementsChannel(
				eventCreator(this.props, this.state, this.session, this.journeyId, ...args),
			)(createAnalyticsEvent);
		}
	};

	private startSession = () => {
		this.session = startSession();
		this.journeyId = this.getSessionId();
		this.fireEvent(focusEvent);
	};

	componentDidMount(): void {
		const { open, search } = this.props;
		// load options when the picker open
		if (open) {
			if (!this.session) {
				this.startSession();
			}
			this.executeLoadOptions(search);
		}
	}

	componentDidUpdate(prevProps: UserPickerProps, prevState: UserPickerState): void {
		const { menuIsOpen, options, resolving, count, inputValue } = this.state;

		// Close menu when isFooterFocused or isHeaderFocused changes from true to false
		if (
			menuIsOpen &&
			((prevProps.isFooterFocused === true && this.props.isFooterFocused === false) ||
				(prevProps.isHeaderFocused === true && this.props.isHeaderFocused === false)) &&
			!this.shouldKeepMenuOpen()
		) {
			this.resetInputState();
			callCallback(this.props.onClose, this.getSessionId());
			this.setState({
				menuIsOpen: false,
				options: [],
			});
		}

		// Create a new session when the menu is opened and there is no session
		if (menuIsOpen && !prevState.menuIsOpen && !this.session) {
			this.startSession();
		}

		// Load options when user picker opens or when input value changes
		if ((menuIsOpen && !prevState.menuIsOpen) || inputValue !== prevState.inputValue) {
			this.executeLoadOptions(inputValue);
		}

		if (!menuIsOpen && prevState.menuIsOpen && this.session) {
			this.fireEvent(cancelEvent, prevState);
			this.session = undefined;
		}

		if (
			menuIsOpen &&
			((!prevState.menuIsOpen && options.length > 0) || options.length !== prevState.options.length)
		) {
			this.fireEvent(searchedEvent);
		}

		if (this.state.inputValue !== prevState.inputValue) {
			if (this.session) {
				this.session.inputChangeTime = Date.now();
			}
		}

		if (
			menuIsOpen &&
			(!prevProps.loadOptions || prevState.menuIsOpen) &&
			count === 0 &&
			!resolving &&
			[UFOExperienceState.STARTED.id, UFOExperienceState.IN_PROGRESS.id].includes(
				this.optionsShownUfoExperienceInstance.state.id,
			)
		) {
			this.optionsShownUfoExperienceInstance.success();
		}
	}

	private handleKeyDown = (event: React.KeyboardEvent) => {
		if (!this.isMenuOpenOnClickForSingleSelect) {
			// Escape
			if (event.keyCode === 27) {
				this.blur();
			}

			// Space
			if (event.keyCode === 32 && !this.state.inputValue) {
				event.preventDefault();
				!this.props.disableInput && this.setState({ inputValue: ' ' });
			}
		}
		if (this.session) {
			this.session.lastKey = event.keyCode;
			switch (event.keyCode) {
				// KeyUp 38
				case 38:
					this.session.upCount++;
					break;
				// KeyDown 40
				case 40:
					this.session.downCount++;
					break;
			}
		}
		this.props.onKeyDown && this.props.onKeyDown(event);

		if (event.key === 'Escape') {
			this.setState({
				menuIsOpen: false,
				options: [],
			});
			this.props.setIsFooterFocused && this.props.setIsFooterFocused(false);
			this.props.setIsHeaderFocused && this.props.setIsHeaderFocused(false);
		}

		if (event.key === 'Tab' && this.props.setIsFooterFocused && this.props.footer) {
			this.props.setIsFooterFocused(true);
		}

		if (event.key === 'Tab' && this.props.setIsHeaderFocused && this.props.header) {
			this.props.setIsHeaderFocused(true);
		}
	};

	handleClearIndicatorHover = (hoveringClearIndicator: boolean): void => {
		this.setState({ hoveringClearIndicator });
	};

	private getOptionsOld = (): Option[] | GroupedOptions[] => {
		const options = getOptions(this.state.options) || [];
		const { maxOptions, isMulti, groupByTypeOrder } = this.props;
		if (maxOptions === 0) {
			return [];
		}
		if (maxOptions && maxOptions > 0 && maxOptions < options.length) {
			const { value } = this.state;
			let filteredOptions = options;
			// Filter out previously selected options
			if (isMulti && Array.isArray(value)) {
				const valueIds: string[] = value.map((item) => item.data.id);
				filteredOptions = options.filter((option) => valueIds.indexOf(option.data.id) === -1);
			}
			return groupByTypeOrder
				? groupOptionsByType(filteredOptions.slice(0, maxOptions), groupByTypeOrder)
				: filteredOptions.slice(0, maxOptions);
		}

		return groupByTypeOrder ? groupOptionsByType(options, groupByTypeOrder) : options;
	};

	private getOptionsNew = (): Option[] | GroupedOptions[] => {
		const options = getOptions(this.state.options) || [];
		const { maxOptions, isMulti, groupByTypeOrder, customGroupLabels } = this.props;
		if (maxOptions === 0) {
			return [];
		}
		if (maxOptions && maxOptions > 0 && maxOptions < options.length) {
			const { value } = this.state;
			let filteredOptions = options;
			// Filter out previously selected options
			if (isMulti && Array.isArray(value)) {
				const valueIds: string[] = value.map((item) => item.data.id);
				filteredOptions = options.filter((option) => valueIds.indexOf(option.data.id) === -1);
			}
			return groupByTypeOrder
				? groupOptionsByType(
						filteredOptions.slice(0, maxOptions),
						groupByTypeOrder,
						customGroupLabels,
					)
				: filteredOptions.slice(0, maxOptions);
		}

		return groupByTypeOrder
			? groupOptionsByType(options, groupByTypeOrder, customGroupLabels)
			: options;
	};

	private getOptions = (): Option[] | GroupedOptions[] => {
		return fg('jsm-wfo-assignee-recommendation-on-queues')
			? this.getOptionsNew()
			: this.getOptionsOld();
	};

	private getAppearance = (): Appearance =>
		this.props.appearance ? this.props.appearance : 'normal';

	get ariaProps(): Partial<AriaAttributes> {
		const ariaLabels = Object.keys(this.props).filter((key) => key.startsWith('aria-'));
		const props: { [key: string]: any } = { ...this.props };
		return ariaLabels.reduce<Partial<AriaAttributes>>(
			(obj, key) => ({ ...obj, [key]: props[key] }),
			{},
		);
	}

	private handleClickDraggableParentComponent = () => {
		if (this.state.initialFocusHandled) {
			// As <Select /> is already focused, calling this.selectRef.current.focus() again no longer triggers its own handlers (i.e. onMenuOpen, onMenuClose)
			// We have to manually handle the open and close states of the dropdown menu based on a user's click from this point onwards to prevent the bug
			if (this.state.menuIsOpen) {
				this.handleClose();
			} else {
				this.handleOpen();
			}
			// escaping the below condition when openMenuOnClick is true otherwise menu own't open on click
		} else if (!this.state.menuIsOpen && !this.isMenuOpenOnClickForSingleSelect) {
			// Trigger focus state when ValueContainer is clicked for the first time
			// The focused state will then invoke <Select /> instance's own handlers (e.g. onMenuOpen, onMenuClose)
			// to manage the state of the dropdown menu
			this.focus();
			this.setState(() => ({ initialFocusHandled: true }));
		}
	};

	private shouldKeepMenuOpen = () =>
		(Boolean(!!this.props.footer) && Boolean(this.props.isFooterFocused)) ||
		(Boolean(!!this.props.header) && Boolean(this.props.isHeaderFocused));

	render(): React.JSX.Element {
		const {
			isMulti,
			isLoading,
			subtle,
			noBorder,
			placeholder,
			isClearable,
			isDisabled,
			isInvalid,
			clearValueLabel,
			menuMinWidth,
			menuPortalTarget,
			loadOptionsErrorMessage,
			addMoreMessage,
			noOptionsMessage,
			footer,
			disableInput,
			components,
			pickerProps,
			SelectComponent,
			styles,
			autoFocus,
			fieldId,
			inputId,
			maxPickerHeight,
			textFieldBackgroundColor,
			ariaLabelledBy,
			ariaDescribedBy,
			ariaLive,
			ariaLabel,
			name,
			header,
			required,
			UNSAFE_hasDraggableParentComponent,
			placeholderAvatar,
		} = this.props;

		const { count, hoveringClearIndicator, menuIsOpen, value, inputValue, resolving, showError } =
			this.state;
		const appearance = this.getAppearance();
		// these props override the default select menu open and close
		const menuOpenDeciderProps = {
			...(!this.isMenuOpenOnClickForSingleSelect && {
				menuIsOpen,
				blurInputOnSelect: !isMulti,
				openMenuOnFocus: this.props.openMenuOnFocus ?? true,
			}),
		};

		const {
			'aria-labelledby': ariaLabelledByStandard,
			'aria-describedby': ariaDescribedByStandard,
			...restAriaProps
		} = this.ariaProps;

		return (
			<SelectComponent
				name={name}
				value={value}
				autoFocus={autoFocus !== undefined ? autoFocus : menuIsOpen}
				labelId={ariaLabelledBy ?? ariaLabelledByStandard}
				descriptionId={ariaDescribedBy ?? ariaDescribedByStandard}
				aria-live={ariaLive}
				aria-required={required} // This has been added as a safety net.
				required={required}
				ref={this.handleSelectRef}
				isMulti={isMulti}
				options={this.getOptions()}
				styles={styles}
				enableAnimation={isMulti}
				components={components}
				inputValue={inputValue}
				isLoading={count > 0 || resolving || isLoading}
				loadingMessage={loadingMessage}
				menuPlacement="auto"
				placeholder={placeholder ?? <FormattedMessage {...messages.placeholder} />}
				label={ariaLabel}
				addMoreMessage={addMoreMessage}
				classNamePrefix={classNamePrefix}
				hoveringClearIndicator={hoveringClearIndicator}
				appearance={appearance}
				isClearable={isClearable}
				noBorder={noBorder}
				subtle={subtle}
				closeMenuOnSelect={!isMulti}
				noOptionsMessage={
					showError
						? loadOptionsErrorMessage
						: typeof noOptionsMessage === 'function'
							? noOptionsMessage
							: () => noOptionsMessage
				}
				footer={footer}
				isDisabled={isDisabled}
				isInvalid={isInvalid}
				isFocused={menuIsOpen}
				backspaceRemovesValue={isMulti}
				filterOption={null} // disable local filtering
				clearValueLabel={clearValueLabel}
				menuMinWidth={menuMinWidth}
				menuPortalTarget={menuPortalTarget}
				disableInput={disableInput || isDisabled}
				instanceId={fieldId}
				inputId={inputId}
				onChange={this.handleChange}
				onMenuOpen={this.handleOpen}
				onFocus={this.handleFocus}
				onBlur={this.handleBlur}
				onMenuClose={this.handleClose}
				onInputChange={this.handleInputChange}
				onClearIndicatorHover={this.handleClearIndicatorHover}
				onKeyDown={this.handleKeyDown}
				maxPickerHeight={maxPickerHeight}
				textFieldBackgroundColor={textFieldBackgroundColor}
				header={header}
				placeholderAvatar={placeholderAvatar}
				{...restAriaProps}
				{...pickerProps}
				{...(UNSAFE_hasDraggableParentComponent && {
					onValueContainerClick: this.handleClickDraggableParentComponent,
				})}
				{...menuOpenDeciderProps}
			/>
		);
	}
}

export const BaseUserPicker: React.ForwardRefExoticComponent<
	Pick<
		Omit<
			{
				addMoreMessage?: string;
				allowEmail?: boolean;
				anchor?: React.ComponentType<any>;
				appearance?: Appearance;
				ariaDescribedBy?: string;
				ariaLabel?: string;
				ariaLabelledBy?: string;
				ariaLive?: 'polite' | 'off' | 'assertive';
				autoFocus?: boolean;
				captureMenuScroll?: boolean;
				clearValueLabel?: string;
				closeMenuOnScroll?: boolean | EventListener;
				components?: SelectComponentsConfig<OptionData, boolean>;
				customGroupAnalyticsLabels?: Partial<Record<NonNullable<OptionData['type']>, string>>;
				customGroupLabels?: Partial<Record<NonNullable<OptionData['type']>, React.ReactNode>>;
				defaultValue?: DefaultValue;
				disableInput?: boolean;
				emailLabel?: string;
				fieldId: string | null;
				footer?: React.ReactNode;
				forwardedRef?: React.ForwardedRef<UserPickerRef>;
				groupByTypeOrder?: NonNullable<OptionData['type']>[];
				header?: React.ReactNode;
				height?: number | string;
				includeTeamsUpdates?: boolean;
				inputId?: string;
				isClearable?: boolean;
				isDisabled?: boolean;
				isFooterFocused?: boolean;
				isHeaderFocused?: boolean;
				isInvalid?: boolean;
				isLoading?: boolean;
				isMulti?: boolean;
				isValidEmail?: EmailValidator;
				loadOptions?: LoadOptions;
				loadOptionsErrorMessage?: (value: { inputValue: string }) => React.ReactNode;
				loadUserSource?: LoadUserSource;
				maxOptions?: number;
				maxPickerHeight?: number;
				menuIsOpen?: boolean;
				menuMinWidth?: number;
				menuPortalTarget?: HTMLElement;
				menuPosition?: 'absolute' | 'fixed';
				menuShouldBlockScroll?: boolean;
				name?: string;
				noBorder?: boolean;
				noOptionsMessage?:
					| ((value: { inputValue: string }) => string | null | React.ReactNode)
					| null
					| React.ReactNode;
				onBlur?: OnPicker;
				onChange?: OnChange;
				onClear?: OnPicker;
				onClose?: OnPicker;
				onFocus?: OnPicker;
				onInputChange?: OnInputChange;
				onKeyDown?: (event: React.KeyboardEvent) => void;
				onOpen?: OnPicker;
				onSelection?: OnOption;
				open?: boolean;
				openMenuOnClick?: boolean;
				openMenuOnFocus?: boolean;
				options?: OptionData[];
				placeholder?: React.ReactNode;
				placeholderAvatar?: 'person' | 'team';
				popupSelectProps?: PopupSelectProps<OptionData>;
				required?: boolean;
				search?: string;
				setIsFooterFocused?: React.Dispatch<React.SetStateAction<boolean>>;
				setIsHeaderFocused?: React.Dispatch<React.SetStateAction<boolean>>;
				showClearIndicator?: boolean;
				strategy?: 'fixed' | 'absolute';
				styles?: StylesConfig;
				subtle?: boolean;
				suggestEmailsForDomain?: string;
				textFieldBackgroundColor?: boolean;
				UNSAFE_hasDraggableParentComponent?: boolean;
				value?: Value;
				width?: number | string;
			} & {
				components: any;
				name?: string;
				pickerProps?: any;
				SelectComponent: React.ComponentType<any>;
				styles: any;
				// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required
				/**
				 * @deprecated This is a temporary prop to enable user-pickers to work in Draggable elements in react-beautiful-dnd.
				 * See https://product-fabric.atlassian.net/browse/DSP-15701 for more details.
				 * It may be removed in a future minor or patch when a longer-term workaround is found.
				 */
				UNSAFE_hasDraggableParentComponent?: boolean;
				width: string | number;
			},
			keyof WithAnalyticsEventsProps
		>,
		| 'options'
		| 'noOptionsMessage'
		| 'placeholder'
		| 'addMoreMessage'
		| 'allowEmail'
		| 'anchor'
		| 'appearance'
		| 'ariaDescribedBy'
		| 'ariaLabel'
		| 'ariaLabelledBy'
		| 'ariaLive'
		| 'autoFocus'
		| 'captureMenuScroll'
		| 'clearValueLabel'
		| 'closeMenuOnScroll'
		| 'components'
		| 'defaultValue'
		| 'disableInput'
		| 'emailLabel'
		| 'fieldId'
		| 'footer'
		| 'forwardedRef'
		| 'groupByTypeOrder'
		| 'customGroupLabels'
		| 'customGroupAnalyticsLabels'
		| 'header'
		| 'height'
		| 'includeTeamsUpdates'
		| 'inputId'
		| 'isDisabled'
		| 'isFooterFocused'
		| 'isInvalid'
		| 'isLoading'
		| 'isValidEmail'
		| 'loadOptions'
		| 'loadUserSource'
		| 'maxOptions'
		| 'maxPickerHeight'
		| 'menuIsOpen'
		| 'menuMinWidth'
		| 'menuPortalTarget'
		| 'menuPosition'
		| 'menuShouldBlockScroll'
		| 'name'
		| 'onBlur'
		| 'onChange'
		| 'onClear'
		| 'onClose'
		| 'onFocus'
		| 'onInputChange'
		| 'onKeyDown'
		| 'onOpen'
		| 'onSelection'
		| 'open'
		| 'openMenuOnFocus'
		| 'placeholderAvatar'
		| 'popupSelectProps'
		| 'required'
		| 'search'
		| 'setIsFooterFocused'
		| 'showClearIndicator'
		| 'strategy'
		| 'styles'
		| 'suggestEmailsForDomain'
		| 'UNSAFE_hasDraggableParentComponent'
		| 'value'
		| 'width'
		| 'pickerProps'
		| 'SelectComponent'
	> & {
		isClearable?: boolean | undefined;
		isMulti?: boolean | undefined;
		loadOptionsErrorMessage?: ((value: { inputValue: string }) => React.ReactNode) | undefined;
		noBorder?: boolean | undefined;
		openMenuOnClick?: boolean | undefined;
		openMenuOnFocus?: boolean | undefined;
		subtle?: boolean | undefined;
		textFieldBackgroundColor?: boolean | undefined;
	} & {} & React.RefAttributes<any>
> = withAnalyticsEvents()(BaseUserPickerWithoutAnalytics);
